import { useEffect, useState } from "react";
import { Menu, Phone, X } from "lucide-react";
import { BRAND, NAV_LINKS } from "@/lib/site";
import { Button } from "@/components/ui/button";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const close = () => setOpen(false);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-white/85 border-b border-slate-200/70 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-20 flex items-center justify-between gap-6">
        <a
          href="#top"
          data-testid="header-logo-link"
          className="flex items-center gap-3 group"
          onClick={close}
        >
          <div className="h-11 w-11 rounded-xl bg-white grid place-items-center overflow-hidden shadow-sm ring-1 ring-slate-200">
            <img
              src={BRAND.logo}
              alt="Virtue Registration Services"
              className="h-9 w-9 object-contain"
            />
          </div>
          <div className="leading-tight">
            <div className="font-display font-semibold text-slate-900 text-[15px] tracking-tight">
              Virtue Registrations
            </div>
            <div className="text-[11px] text-slate-500 uppercase tracking-[0.18em]">
              San Diego, CA
            </div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href={BRAND.phoneHref}
            data-testid="header-phone-link"
            className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-slate-900 hover:text-blue-600 transition-colors px-3 py-2"
          >
            <Phone className="h-4 w-4" />
            {BRAND.phone}
          </a>
          <Button
            asChild
            data-testid="header-book-button"
            className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-5 h-10 font-medium shadow-sm hidden sm:inline-flex"
          >
            <a href="#book">Book Appointment</a>
          </Button>
          <button
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            className="lg:hidden h-10 w-10 grid place-items-center rounded-lg border border-slate-200 bg-white/80"
            onClick={() => setOpen((o) => !o)}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-nav"
          className="lg:hidden border-t border-slate-200 bg-white/95 backdrop-blur-xl"
        >
          <div className="px-5 py-5 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={close}
                className="px-3 py-3 rounded-lg text-slate-700 hover:bg-slate-100 font-medium"
              >
                {l.label}
              </a>
            ))}
            <a
              href={BRAND.phoneHref}
              onClick={close}
              className="px-3 py-3 rounded-lg text-slate-700 hover:bg-slate-100 font-medium inline-flex items-center gap-2"
            >
              <Phone className="h-4 w-4" /> {BRAND.phone}
            </a>
            <a
              href="#book"
              onClick={close}
              className="mt-2 px-5 py-3 rounded-lg bg-blue-600 text-white font-semibold text-center"
            >
              Book Appointment
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
